import { Button, Checkbox, Group, Stack, Text, Title } from '@mantine/core'

import type { PaymentMethod, Plan } from '@/services/api/api.types'
import { getPaymentMethodDescription } from '@/utils/paymentMethodHelpers'

import { BillingPlanCard } from './BillingPlanCard'

interface BillingPlanSelectionProps {
  plans: Plan[]
  selectedPlan: string
  autoRefillEnabled: boolean
  thresholdCredits: number
  paymentMethod: PaymentMethod | null
  onPlanSelect: (planId: string) => void
  onAutoRefillChange: (enabled: boolean) => void
  onThresholdChange: (threshold: number) => void
  onSave: () => void
  onCancel: () => void
  isUpdating?: boolean
}

export function BillingPlanSelection({
  plans,
  selectedPlan,
  autoRefillEnabled,
  thresholdCredits: _thresholdCredits,
  paymentMethod,
  onPlanSelect,
  onAutoRefillChange,
  onThresholdChange: _onThresholdChange,
  onSave,
  onCancel,
  isUpdating = false,
}: BillingPlanSelectionProps) {
  return (
    <Stack gap="xl">
      <Stack gap="xs" align="center">
        <Title order={3} ta="center">
          Update Your Plan
        </Title>
        {/* Credit Information */}
        <Text size="sm" c="dimmed">
          <strong>1 credit per user request</strong>
        </Text>
      </Stack>

      {/* Auto-Refill Checkbox */}
      <Group justify="center">
        <Checkbox
          checked={autoRefillEnabled}
          onChange={event => onAutoRefillChange(event.currentTarget.checked)}
          label="Enable auto-refill (automatically purchase more credits when running low)"
          size="md"
          color="indigo"
        />
      </Group>

      {/* Threshold Input */}
      {/* Retaining code in case we want to allow users to change the auto-refill threshold in the future
      autoRefillEnabled && (
        <Group gap="xs" align="center" justify="center">
          <Text size="sm" fw={500}>
            Auto-refill when credits drop below:
          </Text>
          <NumberInput
            value={thresholdCredits}
            onChange={value => onThresholdChange(typeof value === 'number' ? value : 0)}
            disabled={true}
            min={1}
            max={10000}
            step={1}
            size="md"
            variant="unstyled"
            fw={600}
            w={120}
            rightSection={
              <Text size="sm" fw={600}>
                credits
              </Text>
            }
            rightSectionWidth={60}
            styles={{
              input: { textAlign: 'center' },
            }}
          />
        </Group>
      )
      */}

      {/* Plan Cards */}
      <Group
        justify="center"
        align="stretch"
        gap="xs"
        w="100%"
        mx="auto"
        style={{
          flexWrap: 'wrap',
          opacity: autoRefillEnabled ? 1 : 0.5,
          pointerEvents: autoRefillEnabled ? 'auto' : 'none',
        }}>
        {plans
          ?.filter(plan => plan.active)
          .sort((a, b) => a.priceDollars - b.priceDollars)
          .map(plan => (
            <BillingPlanCard
              key={plan.bundleId}
              plan={plan}
              isSelected={selectedPlan === plan.bundleId}
              onSelect={onPlanSelect}
              disabled={!autoRefillEnabled}
            />
          ))}
      </Group>

      {paymentMethod && (
        <Text size="sm" c="dimmed" ta="center">
          Credits will be charged to {getPaymentMethodDescription(paymentMethod)}
        </Text>
      )}

      {/* Action Buttons */}
      <Group justify="space-between" mt="xl">
        <Button variant="light" onClick={onCancel} size="md" disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={(autoRefillEnabled && !selectedPlan) || isUpdating}
          loading={isUpdating}
          size="md"
          color="indigo">
          Save Changes
        </Button>
      </Group>
    </Stack>
  )
}
